import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';

import { AI_CREDITS_EXHAUSTED } from '@flintmint/core-shared/constants';

import { capture } from '@/analytics';
import { useUserSourcesByIds } from '@/hooks/rxdb-models';
import { processingUploadIdsAtom } from '@/state';

/**
 * Mounted once. Records each upload this client sent for processing that processing refused for lack of AI Energy,
 * and stops tracking every upload once its processing settles. Queries nothing while no upload is tracked.
 *
 * The query leaves out archived and deleted sources, so an upload that was in the results and is now missing stops
 * being tracked too. One never seen stays tracked: a just-created source can be missing from the first results.
 */
export const UserSourceRefusalWatcher = () => {
  const [trackedIds, setTrackedIds] = useAtom(processingUploadIdsAtom);
  const { data: sources } = useUserSourcesByIds(trackedIds);
  const seenIds = useRef(new Set<string>());

  useEffect(() => {
    const tracked = sources.filter((source) => trackedIds.includes(source.id));
    const settled = tracked.filter((source) => source.syncStatus !== 'processing');
    const gone = trackedIds.filter((id) => seenIds.current.has(id) && !tracked.some((source) => source.id === id));
    for (const source of tracked) seenIds.current.add(source.id);
    if (!settled.length && !gone.length) return;

    for (const source of settled) {
      if (source.syncStatus === 'error' && source.error === AI_CREDITS_EXHAUSTED) {
        capture('ai_credits_exhausted', { source: 'user_source_upload' });
      }
    }
    const droppedIds = [...settled.map((source) => source.id), ...gone];
    for (const id of droppedIds) seenIds.current.delete(id);
    setTrackedIds((ids) => ids.filter((id) => !droppedIds.includes(id)));
  }, [sources, trackedIds, setTrackedIds]);

  return null;
};
